import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import UFOSegment from "@atlaskit/react-ufo/segment";

import { Adg4Button } from "@/components/adg4/button/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    ADG4CustomerTestimonials,
    TestimonialsTypes
} from "@/patterns/adg4/customer-testimonials/src";
import {
    TestimonialDropbox,
    TestimonialLumen,
    TestimonialRoblox
} from "@/patterns/adg4/customer-testimonials/src/testimonial-logos";

const logoSizeStyles = css({
    "paddingBlockStart": 48,
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".bgshape": {
        background: "#101214 !important"
    },
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        color: "#fff"
    },
    // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-width": {
        // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".logos-section": {
            "width": "100%",
            // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".logo-option": {
                borderColor: "lightgray !important",
                // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                svg: {
                    maxHeight: 42
                }
            }
        }
    }
});

const TestimonialsSection = () => {
    const intl = useIntl();
    const testimonials: TestimonialsTypes[] = [
        {
            name: intl.formatMessage({
                id: "ConfluenceSoftwarePage.v4.testimonials.testimonialDropbox.name",
                defaultMessage: "Damien Davis"
            }),
            logo: <TestimonialDropbox />,
            title: intl.formatMessage({
                id: "ConfluenceSoftwarePage.v4.testimonials.testimonialDropbox.title",
                defaultMessage: "Lead Atlassian Administrator, Dropbox"
            }),
            image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/testimonial-dropbox.webp",
            quote: intl.formatMessage({
                id: "ConfluenceSoftwarePage.v4.testimonials.testimonialDropbox.quote",
                defaultMessage:
                    "Confluence out of the box is a fantastic product, and integrations take it to another level. We can embed videos, media, and all sorts of files and make them accessible across the enterprise."
            }),
            label: "Dropbox"
        },
        {
            name: intl.formatMessage({
                id: "ConfluenceSoftwarePage.v4.testimonials.testimonialRoblox.name",
                defaultMessage: "Joe Cotant"
            }),
            logo: <TestimonialRoblox />,
            title: intl.formatMessage({
                id: "ConfluenceSoftwarePage.v4.testimonials.testimonialRoblox.title",
                defaultMessage: "Senior Technical Program Manager, Roblox"
            }),
            image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/testimonial-roblox.webp",
            quote: intl.formatMessage({
                id: "ConfluenceSoftwarePage.v4.testimonials.testimonialRoblox.quote",
                defaultMessage:
                    "Using smart links within Confluence and Jira makes cross-team collaboration much easier. These integrations have been pivotal in collaboration, productivity, and discoverability."
            }),
            label: "Roblox"
        },
        {
            name: intl.formatMessage({
                id: "ConfluenceSoftwarePage.v4.testimonials.testimonialLumen.name",
                defaultMessage: "Rob Mulvaney"
            }),
            logo: <TestimonialLumen />,
            title: intl.formatMessage({
                id: "ConfluenceSoftwarePage.v4.testimonials.testimonialLumen.title",
                defaultMessage:
                    "Director, Agile Transformation Office, Lumen Technologies"
            }),
            image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/testimonial-lumen.webp",
            quote: intl.formatMessage({
                id: "ConfluenceSoftwarePage.v4.testimonials.testimonialLumen.quote",
                defaultMessage:
                    "Employees across the company use Confluence to document important information like objectives and key results, which helps keep teams aligned across the big-picture goals."
            }),
            label: "Lumen"
        }
    ];

    return (
        <UFOSegment name="confluence-pt-cfind-1234-testimonialsSection">
            <div css={logoSizeStyles}>
                <ADG4CustomerTestimonials
                    testimonials={testimonials}
                    imagePosition="right"
                    testimonialCta={<TestimonialCta />}
                />
            </div>
        </UFOSegment>
    );
};
TestimonialsSection.displayName = "TestimonialsSection";

const ctaContainerStyles = css({
    "alignItems": "center",
    "borderBlockStart": "solid 2px lightblue",
    "flexGrow": "1",
    "height": "100%",
    "paddingInlineEnd": "109px",
    "display": "flex",
    "justifyContent": "flex-end",
    "insetBlockStart": "1px",
    "position": "relative",
    "@media (max-width: 1140px)": {
        border: "0",
        justifyContent: "center",
        marginBlockEnd: "49px",
        padding: 0
    }
});

const TestimonialCta = () => {
    const intl = useIntl();

    const customersLink = useLocalizedHref("/customers");
    const linkText = intl.formatMessage({
        id: "ConfluenceSoftwarePage.v4.testimonials.ctaButton.text",
        defaultMessage: "Explore customer stories"
    });
    return (
        <div css={ctaContainerStyles}>
            <Adg4Button
                href={customersLink}
                variant="secondary"
                color="white"
                dataEventProps={{
                    "data-product-key": "confluence",
                    "data-event": "clicked",
                    "data-event-component": "linkButton",
                    "data-event-container": "testimonialsSection",
                    "data-label": "testimonials-cta",
                    "data-label-english": "Explore customer stories",
                    "data-track-event": "confluence-pt.testimonials.cta"
                }}
            >
                {linkText}
            </Adg4Button>
        </div>
    );
};

export default TestimonialsSection;
